import { useState, useMemo, useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { SiteHeader } from "@/components/site-header"
import { authorizedFetch } from "@/lib/api"
import { usePermissions } from "@/hooks/use-permissions"
import { FilterComboboxLabeled } from "@/pages/smart-gt/SmartGTComponents"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Pagination } from "@/components/ui/pagination"
import {
  FileTextIcon,
  ImageIcon,
  FileIcon,
  ExternalLinkIcon,
  Trash2Icon,
  Loader2Icon,
  SearchIcon,
  PlusIcon,
  PencilIcon,
  PaperclipIcon,
  CalendarIcon,
  XIcon,
} from "lucide-react"

const API_URL  = import.meta.env.VITE_API_URL ?? "http://localhost:3001"
const PAGE_SIZE = 25

// ─── Types ────────────────────────────────────────────────────────────────────

interface Driver {
  id:   string
  name: string
  code: string
}

interface DriverDoc {
  id:         string
  driverId:   string
  driverName: string
  driverDni:  string | null
  fileUrl:    string
  fileKey:    string
  mimeType:   string | null
  notes:      string | null
  createdAt:  string
  signedUrl:  string
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("es-AR", {
    day:    "2-digit",
    month:  "2-digit",
    year:   "numeric",
    hour:   "2-digit",
    minute: "2-digit",
  })
}

type DocType = "image" | "pdf" | "file"

function docType(mimeType: string | null): DocType {
  if (!mimeType) return "file"
  if (mimeType.startsWith("image/"))  return "image"
  if (mimeType === "application/pdf") return "pdf"
  return "file"
}

function fileLabel(key: string) {
  return key.split("/").pop() ?? key
}

const TYPE_BADGE: Record<DocType, { label: string; icon: React.ReactNode; cls: string }> = {
  image: {
    label: "Imagen",
    icon:  <ImageIcon    className="size-3" />,
    cls:   "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400",
  },
  pdf: {
    label: "PDF",
    icon:  <FileTextIcon className="size-3" />,
    cls:   "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400",
  },
  file: {
    label: "Archivo",
    icon:  <FileIcon     className="size-3" />,
    cls:   "bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400",
  },
}

// ─── DocFormSheet ─────────────────────────────────────────────────────────────

interface DocFormSheetProps {
  open:    boolean
  onClose: () => void
  editing: DriverDoc | null
  drivers: Driver[]
  onSaved: () => void
}

function DocFormSheet({ open, onClose, editing, drivers, onSaved }: DocFormSheetProps) {
  const fileRef = useRef<HTMLInputElement>(null)

  const [driverId, setDriverId] = useState("")
  const [file,     setFile]     = useState<File | null>(null)
  const [notes,    setNotes]    = useState("")
  const [saving,   setSaving]   = useState(false)
  const [error,    setError]    = useState<string | null>(null)

  // Reset state each time the sheet opens or switches between create/edit
  useEffect(() => {
    if (open) {
      setDriverId("")
      setFile(null)
      setNotes(editing?.notes ?? "")
      setError(null)
      setSaving(false)
    }
  }, [open, editing])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    if (editing) {
      // Edit mode: PATCH notes only
      setSaving(true)
      try {
        await authorizedFetch(
          `${API_URL}/api/providers/drivers/${editing.driverId}/docs/${editing.id}`,
          {
            method:  "PATCH",
            headers: { "Content-Type": "application/json" },
            body:    JSON.stringify({ notes: notes.trim() || null }),
          }
        )
        onSaved()
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Error al guardar")
      } finally {
        setSaving(false)
      }
      return
    }

    // Create mode: presign → direct S3 upload → save record
    if (!driverId) { setError("Seleccioná un chofer"); return }
    if (!file)     { setError("Seleccioná un archivo"); return }

    setSaving(true)
    try {
      // 1. Get presigned URL
      const presignRes = await authorizedFetch(
        `${API_URL}/api/providers/drivers/${driverId}/docs/presign`,
        {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({
            fileName: file.name,
            mimeType: file.type || "application/octet-stream",
          }),
        }
      ).then(r => r.json()) as { uploadUrl: string; key: string }

      // 2. Upload directly to S3 (plain fetch, NOT authorizedFetch)
      await fetch(presignRes.uploadUrl, {
        method:  "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body:    file,
      })

      // 3. Persist the record
      await authorizedFetch(
        `${API_URL}/api/providers/drivers/${driverId}/docs`,
        {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({
            fileKey:  presignRes.key,
            mimeType: file.type || null,
            notes:    notes.trim() || null,
          }),
        }
      )

      onSaved()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Error al guardar")
    } finally {
      setSaving(false)
    }
  }

  const isEdit         = editing !== null
  const selectedDriver = drivers.find(d => d.id === driverId)

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="sm:max-w-md overflow-y-auto flex flex-col">
        <SheetHeader>
          <SheetTitle>{isEdit ? "Editar remito" : "Nuevo remito"}</SheetTitle>
          <SheetDescription>
            {isEdit
              ? "Modificá las notas del documento."
              : "Subí un archivo asociado a un chofer."}
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4 pb-4 flex-1">

          {isEdit ? (
            // Edit mode: readonly filename
            <div className="grid gap-1.5">
              <Label>Archivo</Label>
              <p className="font-mono text-sm text-muted-foreground bg-muted rounded px-3 py-2 break-all">
                {fileLabel(editing.fileKey)}
              </p>
            </div>
          ) : (
            // Create mode: driver select + file picker
            <>
              <div className="grid gap-1.5">
                <Label htmlFor="doc-driver">
                  Chofer <span className="text-destructive">*</span>
                </Label>
                <FilterComboboxLabeled
                  value={driverId}
                  onChange={setDriverId}
                  options={drivers.map(d => ({ label: `${d.name} (${d.code})`, value: d.id }))}
                  placeholder="Buscar chofer..."
                />
              </div>

              <div className="grid gap-1.5">
                <Label>
                  Archivo <span className="text-destructive">*</span>
                </Label>
                <input
                  ref={fileRef}
                  type="file"
                  className="hidden"
                  accept="image/*,.pdf,application/pdf,.doc,.docx,application/msword,.xlsx,.xls"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  className="w-full justify-start gap-2"
                >
                  <PaperclipIcon className="size-4" />
                  {file ? file.name : "Seleccionar archivo..."}
                </Button>
              </div>
            </>
          )}

          <div className="grid gap-1.5">
            <Label htmlFor="doc-notes">Notas</Label>
            <textarea
              id="doc-notes"
              value={notes}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
              maxLength={1000}
              rows={4}
              placeholder="Observaciones opcionales..."
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
            />
          </div>

          {error && (
            <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">
              {error}
            </p>
          )}

          <SheetFooter className="px-0 mt-auto pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={saving}>
              {saving
                ? "Guardando..."
                : isEdit ? "Guardar cambios" : "Crear remito"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function DriverRemitosPage() {
  const qc      = useQueryClient()
  const { can } = usePermissions()

  const [filterDriverId, setFilterDriverId] = useState("")
  const [search,         setSearch]         = useState("")
  const [dateFrom,       setDateFrom]       = useState("")
  const [dateTo,         setDateTo]         = useState("")
  const [page,           setPage]           = useState(1)
  const [sheetOpen,      setSheetOpen]      = useState(false)
  const [editing,        setEditing]        = useState<DriverDoc | null>(null)
  const [confirmDelete,  setConfirmDelete]  = useState<DriverDoc | null>(null)
  const [deletingId,     setDeletingId]     = useState<string | null>(null)
  const [deleteError,    setDeleteError]    = useState<string | null>(null)

  // Reset page on any filter change
  useEffect(() => { setPage(1) }, [search, filterDriverId, dateFrom, dateTo])

  // ── Queries ──────────────────────────────────────────────────────────────────

  const { data: drivers = [] } = useQuery({
    queryKey: ["providers", "drivers", "active"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/providers/drivers?deleted=false&pageSize=500`)
      const json = await res.json() as { data: Driver[] }
      return json.data
    },
    staleTime: 60_000,
  })

  const { data: docs = [], isLoading: loadingDocs } = useQuery({
    queryKey: ["driver-docs", filterDriverId],
    queryFn:  async () => {
      const url = filterDriverId
        ? `${API_URL}/api/providers/driver-docs?driverId=${filterDriverId}`
        : `${API_URL}/api/providers/driver-docs`
      const res = await authorizedFetch(url)
      return res.json() as Promise<DriverDoc[]>
    },
    staleTime: 30_000,
  })

  // ── Client-side filter + pagination ──────────────────────────────────────────

  const filtered = useMemo(() => {
    let result = docs

    const term = search.toLowerCase().trim()
    if (term) {
      result = result.filter(d =>
        (d.notes ?? "").toLowerCase().includes(term) ||
        fileLabel(d.fileKey).toLowerCase().includes(term)
      )
    }

    if (dateFrom) {
      const from = new Date(dateFrom)
      from.setHours(0, 0, 0, 0)
      result = result.filter(d => new Date(d.createdAt) >= from)
    }

    if (dateTo) {
      const to = new Date(dateTo)
      to.setHours(23, 59, 59, 999)
      result = result.filter(d => new Date(d.createdAt) <= to)
    }

    return result
  }, [docs, search, dateFrom, dateTo])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const paged      = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  // ── Handlers ─────────────────────────────────────────────────────────────────

  function openCreate() {
    setEditing(null)
    setSheetOpen(true)
  }

  function openEdit(doc: DriverDoc) {
    setEditing(doc)
    setSheetOpen(true)
  }

  function closeSheet() {
    setSheetOpen(false)
    setEditing(null)
  }

  async function handleDelete(doc: DriverDoc) {
    setDeletingId(doc.id)
    setConfirmDelete(null)
    setDeleteError(null)
    try {
      await authorizedFetch(
        `${API_URL}/api/providers/drivers/${doc.driverId}/docs/${doc.id}`,
        { method: "DELETE" }
      )
      qc.invalidateQueries({ queryKey: ["driver-docs"] })
    } catch (e: unknown) {
      setDeleteError(e instanceof Error ? e.message : "Error al eliminar")
    } finally {
      setDeletingId(null)
    }
  }

  const activeFilterDriver = drivers.find(d => d.id === filterDriverId)

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      <SiteHeader title="Remitos de Choferes" />

      <div className="flex-1 p-4 pt-6 w-full flex flex-col gap-4">

        {/* ── Toolbar ─────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 flex-wrap">

          {/* Driver filter */}
          <FilterComboboxLabeled
            value={filterDriverId}
            onChange={(v) => { setFilterDriverId(v); setSearch("") }}
            options={drivers.map(d => ({ label: `${d.name} (${d.code})`, value: d.id }))}
            placeholder="Todos los choferes..."
            className="w-72"
          />

          {/* Search */}
          <div className="relative w-64">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar por notas o archivo..."
              className="pl-8"
            />
          </div>

          {/* Date range */}
          <div className="flex items-center gap-1.5">
            <CalendarIcon className="size-4 text-muted-foreground shrink-0" />
            <div className="relative">
              <Input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                className="w-44 pr-7"
              />
              {dateFrom && (
                <button
                  type="button"
                  onClick={() => setDateFrom("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <XIcon className="size-3.5" />
                </button>
              )}
            </div>
            <span className="text-muted-foreground text-sm">—</span>
            <div className="relative">
              <Input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                min={dateFrom || undefined}
                className="w-44 pr-7"
              />
              {dateTo && (
                <button
                  type="button"
                  onClick={() => setDateTo("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <XIcon className="size-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* New button */}
          <Button onClick={openCreate} className="ml-auto gap-2">
            <PlusIcon className="size-4" />
            Nuevo remito
          </Button>
        </div>

        {/* ── Table ───────────────────────────────────────────────────────── */}
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10 text-center">#</TableHead>
                <TableHead className="w-52">Chofer</TableHead>
                <TableHead className="w-36">Archivo</TableHead>
                <TableHead>Notas</TableHead>
                <TableHead className="w-40">Fecha</TableHead>
                <TableHead className="w-16 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loadingDocs ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-14 text-muted-foreground">
                    <Loader2Icon className="size-5 animate-spin mx-auto mb-2" />
                    Cargando remitos...
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-14 text-muted-foreground">
                    <FileTextIcon className="size-9 mx-auto mb-2 opacity-20" />
                    {search || filterDriverId || dateFrom || dateTo
                      ? "Sin resultados para esa búsqueda"
                      : "No hay remitos cargados"}
                  </TableCell>
                </TableRow>
              ) : paged.map((doc, idx) => {
                const type  = docType(doc.mimeType)
                const badge = TYPE_BADGE[type]
                const rowNum = (page - 1) * PAGE_SIZE + idx + 1
                return (
                  <TableRow key={doc.id}>

                    {/* # */}
                    <TableCell className="text-center text-xs text-muted-foreground tabular-nums">
                      {rowNum}
                    </TableCell>

                    {/* Driver — plain link */}
                    <TableCell>
                      <Link
                        to={`/drivers/${doc.driverId}`}
                        className="text-sm hover:underline leading-tight"
                      >
                        {doc.driverName}
                      </Link>
                      {doc.driverDni && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          DNI {doc.driverDni}
                        </p>
                      )}
                    </TableCell>

                    {/* Archivo: type badge + view button */}
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Badge
                          variant="outline"
                          className={`gap-1 text-[10px] px-1.5 py-0 shrink-0 ${badge.cls}`}
                        >
                          {badge.icon}
                          {badge.label}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-6 shrink-0"
                          onClick={() => window.open(doc.signedUrl, "_blank")}
                          title="Ver / descargar archivo"
                        >
                          <ExternalLinkIcon className="size-3.5" />
                        </Button>
                      </div>
                    </TableCell>

                    {/* Notes — first 30 chars, full text on hover */}
                    <TableCell className="text-sm">
                      {doc.notes ? (
                        <span
                          title={doc.notes.length > 30 ? doc.notes : undefined}
                          className="cursor-default"
                        >
                          {doc.notes.length > 30
                            ? `${doc.notes.slice(0, 30)}…`
                            : doc.notes}
                        </span>
                      ) : (
                        <span className="text-muted-foreground italic text-xs">—</span>
                      )}
                    </TableCell>

                    {/* Date */}
                    <TableCell className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                      {formatDate(doc.createdAt)}
                    </TableCell>

                    {/* Actions: edit + delete only */}
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          onClick={() => openEdit(doc)}
                          title="Editar notas"
                        >
                          <PencilIcon className="size-4" />
                        </Button>
                        {can("write", "drivers") && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8 text-destructive hover:text-destructive"
                            onClick={() => setConfirmDelete(doc)}
                            disabled={deletingId === doc.id}
                            title="Eliminar"
                          >
                            {deletingId === doc.id
                              ? <Loader2Icon className="size-4 animate-spin" />
                              : <Trash2Icon  className="size-4" />
                            }
                          </Button>
                        )}
                      </div>
                    </TableCell>

                  </TableRow>
                )
              })}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={filtered.length}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          )}
        </div>

        {/* Delete error */}
        {deleteError && (
          <p className="text-sm text-destructive">{deleteError}</p>
        )}

      </div>

      {/* ── Doc form sheet (create + edit) ────────────────────────────────── */}
      <DocFormSheet
        open={sheetOpen}
        onClose={closeSheet}
        editing={editing}
        drivers={drivers}
        onSaved={() => {
          closeSheet()
          qc.invalidateQueries({ queryKey: ["driver-docs"] })
        }}
      />

      {/* ── Delete confirmation dialog ─────────────────────────────────────── */}
      <Dialog
        open={!!confirmDelete}
        onOpenChange={open => { if (!open) setConfirmDelete(null) }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar remito</DialogTitle>
            <DialogDescription>
              Esta acción elimina el archivo de S3 y no se puede deshacer.
              {confirmDelete?.notes && (
                <> · <strong className="text-foreground">{confirmDelete.notes}</strong></>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={!!deletingId}
              onClick={() => confirmDelete && handleDelete(confirmDelete)}
            >
              {deletingId
                ? <><Loader2Icon className="size-4 animate-spin mr-2" />Eliminando...</>
                : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
